import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Paper,
  LinearProgress,
  Button,
  Chip,
  alpha,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  People,
  Assignment,
  Image,
  School,
  CheckCircle,
  BarChart,
  Info,
  CameraAlt,
  TrendingUp,
  Biotech,
  DataUsage,
  ShowChart,
  BubbleChart,
  ArrowForwardIos,
  NewReleases,
  ExpandMore,
  ExpandLess,
  FiberManualRecord,
  CheckCircleOutline,
  BugReport,
  AutoAwesome,
  TrendingUpOutlined,
  Update,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../../app/hooks/useSubscription";
import { Item } from "semantic-ui-react";

interface DashboardItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
}

const dashboardItems: DashboardItem[] = [
  
  {
    title: "Patients",
    description: "Manage patient profiles and histories",
    icon: <People fontSize="large" />,
    path: "/patients",
  },
 
  {
    title: "Images",
    description: "Upload and review medical images",
    icon: <Image fontSize="large" />,
    path: "/images",
  },
  // {
  //   title: "Onboarding",
  //   description: "Customize workflows and preferences",
  //   icon: <School fontSize="large" />,
  //   path: "/onboarding",
  // },
  // {
  //   title: "Finalized Reports",
  //   description: "Review completed and signed-off cases",
  //   icon: <CheckCircle fontSize="large" />,
  //   path: "/finalizedreports",
  // },
   {
    title: "About",
    description: "View Your account and subscription details",
    icon: <Assignment fontSize="large" />,
    path: "/account",
  }
 
];

const dashboardItems2: DashboardItem[] = [
  
  {
    title: "Patients",
    description: "Add patient profile and then scan skin condition",
    icon: <People fontSize="large" />,
    path: "/patients",
  },
 
];


export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [scanActive, setScanActive] = React.useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [versionExpanded, setVersionExpanded] = useState(false);

  // Version and changelog data
  const currentVersion = "2.5.1";
  const versionHistory = [
     {
      version: "2.5.1",
      date: "Feb 16, 2026",
      isCurrent: true,
      changes: [
        { type: "feature", text: "New patient Space with improvements" },
        { type: "improvement", text: "Additional back Button in Case Details View" },
        { type: "bug", text: "Orphaned cases handling corrected" },
       
      ],
    },
    {
      version: "2.5.0",
      date: "Feb 7, 2026",
      isCurrent: true,
      changes: [
        { type: "feature", text: "Patient ID condensed display with copy-to-clipboard" },
        { type: "feature", text: "Simplified report with simpler AI enhancements" },
        { type: "feature", text: "Load More button for per-patient cases pagination" },
        { type: "feature", text: "Team Partners coming soon section" },
         { type: "feature", text: "Export to Word to allow for manual corrections" },
        { type: "improvement", text: "Upgrade prompt for team member limits" },
        { type: "improvement", text: "Enhanced PDF fonts with Helvetica-style for Greek text" },
      ],
    },
    {
      version: "2.4.0",
      date: "Feb 5, 2026",
      changes: [
        { type: "feature", text: "Report count badges in Case Image Gallery" },
        { type: "feature", text: "Glassmorphism design for modern UI elements" },
        { type: "bugfix", text: "Fixed 'Attached Images' appearing in preview mode" },
        { type: "improvement", text: "Simplified CaseBlocksRenderer UI" },
      ],
    },
    {
      version: "2.3.0",
      date: "Jan 28, 2026",
      changes: [
        { type: "feature", text: "Team Management with nurse account creation" },
        { type: "feature", text: "Subscription tier limits for team members" },
        { type: "improvement", text: "Enhanced account management interface" },
      ],
    },
  ];

  const {
    hasActiveSubscription,
    isAdvanced,
    isProfessional,
    hasSubscription,
    isAdmin,
    loading: loading2,
  } = useSubscription();

  // Simulated scan progress
  useEffect(() => {
    if (scanActive) {
      const interval = setInterval(() => {
        setScanProgress((prev) => {
          if (prev >= 100) {
            setScanActive(false);
            return 0;
          }
          return prev + 5;
        });
      }, 150);
      return () => clearInterval(interval);
    }
  }, [scanActive]);

  // Navigate to ChatDomain after scan completes
  useEffect(() => {
    if (scanProgress === 100 && scanActive) {
      setTimeout(() => {
        navigate("/ChatDomain");
      }, 500); // slight delay for UX
    }
  }, [scanProgress, scanActive, navigate]);

  // Simulated AI stats
  const aiStats = {
    diagnosesAssisted: 5000,
    accuracyRate: 89,
    averageTime: "2.3 min",
    recentInsights: [
      "Melanoma detection improved by 12%",
      "New pattern recognition for psoriasis",
      "AI-assisted triage reduced time by 30%",
    ],
  };

  // Simulated epidemiology stats
  const epidemiologyStats = {
    activeCases: 12843,
    trendsIdentified: 18,
    regionsMonitored: 27,
    lastUpdated: "2 hours ago",
  };

  // Recent research topics
  const recentResearch = [
    {
      title: "Noetiv Skin Engine",
      tag: "AI",
      link: "https://www.sciencedirect.com/science/article/pii/S1386505625001157",
    },
    {
      title: "Augmented Health Applications",
      tag: "Process",
      link: "https://www.mdpi.com/2079-9292/13/2/320",
    },
    {
      title: "Optimization",
      tag: "ML",
      link: "https://www.sciencedirect.com/science/article/pii/S0950705125010202",
    },
  ];

  // Animation states for under construction components
  const [doctorProgress, setDoctorProgress] = useState(0);
  const [epiProgress, setEpiProgress] = useState(0);

  // Under construction animations
  useEffect(() => {
    const doctorTimer = setInterval(() => {
      setDoctorProgress((prevProgress) =>
        prevProgress >= 100 ? 0 : prevProgress + 1,
      );
    }, 800);

    const epiTimer = setInterval(() => {
      setEpiProgress((prevProgress) =>
        prevProgress >= 100 ? 0 : prevProgress + 1,
      );
    }, 600);

    return () => {
      clearInterval(doctorTimer);
      clearInterval(epiTimer);
    };
  }, []);

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1, sm: 2, md: 3 },
        backgroundImage: isDarkMode
          ? "radial-gradient(circle, rgba(35,35,40,1) 0%, rgba(28,28,33,1) 100%)"
          : "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(240,249,255,1) 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative DNA helix pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          opacity: isDarkMode ? 0.07 : 0.04,
          backgroundImage: isDarkMode
            ? 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="0.5" fill-rule="evenodd"/%3E%3C/svg%3E")'
            : 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%2303a9f4" fill-opacity="0.5" fill-rule="evenodd"/%3E%3C/svg%3E")',
          backgroundSize: "30px 30px",
          zIndex: -1,
        }}
      />

      {/* State-of-the-Art Version Card */}
      <Paper
        elevation={6}
        sx={{
          mb: 3,
          borderRadius: 4,
          overflow: "hidden",
          background: isDarkMode
            ? `linear-gradient(135deg, ${alpha("#6366f1", 0.15)} 0%, ${alpha("#8b5cf6", 0.15)} 100%)`
            : `linear-gradient(135deg, ${alpha("#6366f1", 0.08)} 0%, ${alpha("#8b5cf6", 0.08)} 100%)`,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: "relative",
          backdropFilter: "blur(20px)",
          boxShadow: isDarkMode
            ? `0 20px 60px ${alpha("#000", 0.4)}, 0 0 1px ${alpha("#fff", 0.1)} inset`
            : `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}, 0 0 1px ${alpha("#000", 0.05)} inset`,
        }}
      >
        {/* Animated gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)} 25%, transparent 25%, transparent 75%, ${alpha(theme.palette.primary.main, 0.05)} 75%, ${alpha(theme.palette.primary.main, 0.05)}), linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.05)} 25%, transparent 25%, transparent 75%, ${alpha(theme.palette.primary.main, 0.05)} 75%, ${alpha(theme.palette.primary.main, 0.05)})`,
            backgroundSize: "20px 20px",
            backgroundPosition: "0 0, 10px 10px",
            opacity: 0.3,
            pointerEvents: "none",
          }}
        />

        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.8 },
                  },
                }}
              >
                <NewReleases sx={{ fontSize: 32, color: "white" }} />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Platform Updates
                  </Typography>
                  <Chip
                    icon={<AutoAwesome sx={{ fontSize: 16 }} />}
                    label="Pilot"
                    size="small"
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.9)}, ${alpha(theme.palette.success.dark, 0.9)})`,
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      px: 1,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Latest features and improvements
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Chip
                label={`v${currentVersion}`}
                icon={<Update />}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "1rem",
                  px: 2,
                  py: 2.5,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  "& .MuiChip-icon": {
                    color: theme.palette.primary.main,
                  },
                }}
              />
              <IconButton
                onClick={() => setVersionExpanded(!versionExpanded)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  transition: "all 0.3s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    transform: "scale(1.05)",
                  },
                }}
              >
                {versionExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>

          {/* Current Version Highlights */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 2,
              mb: 2,
            }}
          >
            {versionHistory[0].changes.slice(0, 3).map((change, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: isDarkMode
                    ? alpha(theme.palette.background.paper, 0.5)
                    : alpha("#fff", 0.7),
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
                  <Box
                    sx={{
                      mt: 0.5,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background:
                        change.type === "feature"
                          ? theme.palette.success.main
                          : change.type === "improvement"
                            ? theme.palette.info.main
                            : theme.palette.warning.main,
                      boxShadow:
                        change.type === "feature"
                          ? `0 0 8px ${alpha(theme.palette.success.main, 0.5)}`
                          : change.type === "improvement"
                            ? `0 0 8px ${alpha(theme.palette.info.main, 0.5)}`
                            : `0 0 8px ${alpha(theme.palette.warning.main, 0.5)}`,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {change.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Dropdown Changelog */}
          <Collapse in={versionExpanded}>
            <Divider sx={{ mb: 2, borderColor: alpha(theme.palette.divider, 0.2) }} />
            <Box sx={{ maxHeight: 400, overflow: "auto" }}>
              {versionHistory.map((version, vIdx) => (
                <Box
                  key={version.version}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    background: version.isCurrent
                      ? alpha(theme.palette.primary.main, 0.08)
                      : isDarkMode
                        ? alpha(theme.palette.background.paper, 0.3)
                        : alpha("#fff", 0.5),
                    border: version.isCurrent
                      ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        v{version.version}
                      </Typography>
                      {version.isCurrent && (
                        <Chip
                          label="Current"
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600, height: 24 }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {version.date}
                    </Typography>
                  </Box>

                  <List dense disablePadding>
                    {version.changes.map((change, cIdx) => (
                      <ListItem key={cIdx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {change.type === "feature" ? (
                            <CheckCircleOutline
                              sx={{ fontSize: 18, color: theme.palette.success.main }}
                            />
                          ) : change.type === "improvement" ? (
                            <TrendingUpOutlined
                              sx={{ fontSize: 18, color: theme.palette.info.main }}
                            />
                          ) : (
                            <BugReport
                              sx={{ fontSize: 18, color: theme.palette.warning.main }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={change.text}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontSize: "0.875rem", fontWeight: 500 },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      </Paper>

  {/* Main dashboard items + Quick Scan in one row */}
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: 3 }}>
        {/* Patients, Images, About items - FREE ACCESS */}
        {dashboardItems.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <Card
              elevation={4}
              sx={{
                borderRadius: { xs: 2, sm: 3 },
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, isDarkMode ? 0.3 : 0.1)}`,
                },
                background: isDarkMode
                  ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`
                  : "linear-gradient(145deg, #ffffff 0%, #f7faff 100%)",
              }}
            >
              <CardActionArea
                sx={{
                  height: "100%",
                  p: { xs: 1, sm: 1.5, md: 2 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                onClick={() => navigate(item.path)}
              >
                <CardContent
                  sx={{
                    width: "100%",
                    p: { xs: 1, sm: 1.5, md: 2 },
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    {React.cloneElement(item.icon as React.ReactElement, {
                      fontSize: isSmallScreen ? "medium" : "large",
                    })}
                    <Typography
                      variant={isSmallScreen ? "subtitle1" : "h6"}
                      sx={{
                        mt: { xs: 0.5, sm: 1 },
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    {!isSmallScreen && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          display: { xs: "none", sm: "block" },
                          fontSize: { sm: "0.75rem", md: "0.875rem" },
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}

        {/* Quick Scan as 4th item in same row - FREE ACCESS */}
        <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={4}
              sx={{
                borderRadius: { xs: 2, sm: 3 },
                height: "100%",
                background: isDarkMode
                  ? "linear-gradient(120deg, rgba(35,50,70,1) 0%, rgba(25,40,60,1) 100%)"
                  : "linear-gradient(120deg, rgba(229,242,255,1) 0%, rgba(233,246,253,1) 100%)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <CardActionArea
                sx={{
                  height: "100%",
                  p: { xs: 1.5, sm: 2 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                onClick={() => setScanActive(true)}
                disabled={scanActive}
              >
                <CardContent sx={{ width: "100%", textAlign: "center" }}>
                  <Box
                    sx={{
                      bgcolor: "primary.main",
                      borderRadius: "50%",
                      p: 1.5,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                      mb: 1,
                    }}
                  >
                    <CameraAlt
                      sx={{
                        color: theme.palette.primary.contrastText,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                      }}
                    />
                  </Box>
                  <Typography
                    variant={isSmallScreen ? "subtitle1" : "h6"}
                    fontWeight="bold"
                    sx={{ mb: 0.5 }}
                  >
                    Quick Scan
                  </Typography>
                  {!isSmallScreen && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        fontSize: { sm: "0.75rem", md: "0.875rem" },
                      }}
                    >
                      AI skin analysis
                    </Typography>
                  )}
                  {scanActive && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={scanProgress}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: isDarkMode
                            ? alpha(theme.palette.background.paper, 0.15)
                            : alpha(theme.palette.primary.main, 0.1),
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                        {scanProgress}%
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
      </Grid>

      {/* Combined Row: Doctor Onboarding, Epidemiology Analytics, and Latest Research */}
      {isAdmin && (
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
          {/* Doctor Onboarding Panel */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                height: "100%",
                background: isDarkMode
                  ? "linear-gradient(120deg, rgba(45,45,70,1) 0%, rgba(35,40,65,1) 100%)"
                  : "linear-gradient(120deg, rgba(240,240,255,1) 0%, rgba(230,242,255,1) 100%)",
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    Doctor Onboarding
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Personalized workflow setup
                  </Typography>
                </Box>
                <Chip
                  label="Coming Soon"
                  color="primary"
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: "medium",
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${alpha(theme.palette.primary.main, 0.8)} 90%)`,
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={doctorProgress}
                    size={60}
                    thickness={4}
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.8rem" }}
                    >
                      {`${Math.round(doctorProgress)}%`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                    Clinical workflow setup and AI-assistance configuration
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
                endIcon={<ArrowForwardIos />}
                onClick={() => navigate("/DoctorOnboarding")}
                sx={{ mt: 2 }}
              >
                View Details
              </Button>
            </Paper>
          </Grid>

          {/* Epidemiology Analytics Panel */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                height: "100%",
                background: isDarkMode
                  ? "linear-gradient(120deg, rgba(40,50,65,1) 0%, rgba(30,45,70,1) 100%)"
                  : "linear-gradient(120deg, rgba(235,250,255,1) 0%, rgba(225,245,255,1) 100%)",
                borderRadius: 2,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    Epidemiology Analytics
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Population health insights
                  </Typography>
                </Box>
                <Chip
                  label="Coming Soon"
                  color="secondary"
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: "medium",
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${alpha(theme.palette.secondary.main, 0.8)} 90%)`,
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={epiProgress}
                    size={60}
                    thickness={4}
                    sx={{ color: theme.palette.secondary.main }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.8rem" }}
                    >
                      {`${Math.round(epiProgress)}%`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                    Disease patterns and treatment efficacy tracking
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                endIcon={<ArrowForwardIos />}
                onClick={() => navigate("/EpidemiologyAnalytics")}
                sx={{ mt: 2 }}
              >
                View Details
              </Button>
            </Paper>
          </Grid>

          {/* Latest Research Panel */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                height: "100%",
                background: isDarkMode
                  ? "linear-gradient(120deg, rgba(40,50,40,1) 0%, rgba(30,45,40,1) 100%)"
                  : "linear-gradient(120deg, rgba(245,255,245,1) 0%, rgba(235,255,250,1) 100%)",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <School color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="medium">
                  Latest Research
                </Typography>
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {recentResearch.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isDarkMode
                        ? alpha(theme.palette.background.paper, 0.15)
                        : alpha(theme.palette.common.white, 0.9),
                      "&:hover": {
                        bgcolor: isDarkMode
                          ? alpha(theme.palette.background.paper, 0.25)
                          : alpha(theme.palette.common.white, 1),
                        boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                      },
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => item.link && window.open(item.link, "_blank")}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ mb: 0.5, fontSize: "0.85rem" }}
                    >
                      {item.title}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Chip
                        label={item.tag}
                        size="small"
                        color={
                          item.tag === "AI"
                            ? "primary"
                            : item.tag === "ML"
                              ? "secondary"
                              : "default"
                        }
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Non-Admin Dashboard Items */}
      {!isAdmin && dashboardItems2.map((item) => (
        <Card
          key={item.title}
          elevation={4}
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, isDarkMode ? 0.3 : 0.1)}`,
            },
            background: isDarkMode
              ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`
              : "linear-gradient(145deg, #ffffff 0%, #f7faff 100%)",
          }}
        >
          <CardActionArea
            sx={{
              height: "100%",
              p: { xs: 1, sm: 1.5, md: 2 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            onClick={() => navigate(item.path)}
          >
            <CardContent
              sx={{
                width: "100%",
                p: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                {React.cloneElement(item.icon as React.ReactElement, {
                  fontSize: isSmallScreen ? "medium" : "large",
                })}
                <Typography
                  variant={isSmallScreen ? "subtitle1" : "h6"}
                  sx={{
                    mt: { xs: 0.5, sm: 1 },
                    fontWeight: 600,
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                  }}
                >
                  {item.title}
                </Typography>
                {!isSmallScreen && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      display: { xs: "none", sm: "block" },
                      fontSize: { sm: "0.75rem", md: "0.875rem" },
                    }}
                  >
                    {item.description}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Box>
  );
}